import { UserRejectedRequestError, type Address, type Hash, type TransactionReceipt } from 'viem'
import { z } from 'zod'

import {
  ACTION_FLOW_EVENTS,
  type ActionFlowReceipt,
  type ActionFlowStepSettlement
} from '@repo/web3'
import type { EventTracker } from '@repo/observability/events'

import type { VaultExitType } from '@/app/api/t/constants'
import { SafeProposalDroppedError } from '@/contexts/Transaction/transaction-errors'
import { logTransactionError } from '@/contexts/Transaction/logTransactionError'
import { logger } from '@/lib/observability/logger'
import type { WalletType } from '@/lib/observability/shared/wallet-type'
import type { TxSource, VariantFeatureFlagKey } from '@/lib/shared/utils/flags.constants'
import { trackTransaction } from '@/lib/observability/client/trackers/transaction'
import { SimulationError } from '@/services/simulate/simulate'

import type { CheckoutStepType } from '../events'
import {
  getCheckoutTrackingProperties,
  maybeTrackStepResolved,
  trackCheckoutStepFailure,
  trackTransactionFlowCancelled
} from './checkout-funnel'
import type { CheckoutAttemptTrackingContext } from './checkout-funnel'
import {
  trackTransactionConfirmed,
  trackTransactionFailed,
  trackTransactionInitiated,
  trackTransactionSigned
} from './transaction'
import { analyticsEventTracker } from '../track'

type SafeFailureEvent =
  | typeof ACTION_FLOW_EVENTS.SAFE_EXPIRED
  | typeof ACTION_FLOW_EVENTS.SAFE_REPLACED
  | typeof ACTION_FLOW_EVENTS.SAFE_PROPOSAL_DROPPED

const SAFE_FAILURE_ERRORS: Record<SafeFailureEvent, (cause: Error | undefined) => Error> = {
  [ACTION_FLOW_EVENTS.SAFE_EXPIRED]: () => new Error('Safe transaction expired'),
  [ACTION_FLOW_EVENTS.SAFE_REPLACED]: () => new Error('Safe transaction replaced'),
  [ACTION_FLOW_EVENTS.SAFE_PROPOSAL_DROPPED]: cause => new SafeProposalDroppedError({ cause })
}

function isSafeFailureEvent(event: string): event is SafeFailureEvent {
  return Object.hasOwn(SAFE_FAILURE_ERRORS, event)
}

export interface ActionFlowTracking {
  /** `tx_outcome` actionType of the final step, e.g. `VaultV2_Deposit`. */
  actionType: string
  /** `tx_outcome` source, mapped from the builder of the transaction the user signed. */
  txSource: TxSource
  /** Set from `getTxFlowTracking` for tx*-flag flows; absent for flows without a tx* flag. */
  flow?: VariantFeatureFlagKey
  /** Set from `getTxFlowTracking` for tx*-flag flows; absent for flows without a tx* flag. */
  txFlag?: TxSource
  /** Vault withdraw-family exit; set by migrated withdraw flows. */
  exitType?: VaultExitType
  chainName: string
  walletType: WalletType
  walletConnector?: string
  vaultAddress?: Address
  /** Absent for flows without a review funnel: funnel events are skipped, `tx_outcome` is kept. */
  checkout?: CheckoutAttemptTrackingContext
  /** Feeds `transaction_signed.simulation_bypassed` at event emission time. */
  getSimulationBypassed?: () => boolean
  /** Product event for a final step that confirms after the engine stopped tracking it (dialog closed or receipt poll failed). */
  onConfirmedLate?: () => void
}

export type TxBuilder = 'sdk' | 'api'

const TX_SOURCE_BY_BUILDER = {
  api: 'api',
  sdk: 'actionflow'
} satisfies Record<TxBuilder, ActionFlowTracking['txSource']>

/** Names the builder of the signed transaction, never the flag value. */
export function txSourceForTracking(builtBy: TxBuilder): ActionFlowTracking['txSource'] {
  return TX_SOURCE_BY_BUILDER[builtBy]
}

export function getCheckoutStepType(params: {
  stepIndex: number
  signatureCount: number
  stepCount: number
}): CheckoutStepType {
  if (params.stepIndex < params.signatureCount) return 'signature'
  if (params.stepIndex >= params.stepCount - 1) return 'final'
  return 'approval'
}

/** @internal */
export function getActionFlowTrackingProperties(tracking: ActionFlowTracking) {
  return {
    action_type: tracking.actionType,
    exit_type: tracking.exitType,
    wallet_type: tracking.walletType,
    wallet_connector: tracking.walletConnector,
    vault_address: tracking.vaultAddress,
    ...(tracking.checkout && getCheckoutTrackingProperties(tracking.checkout))
  }
}

export interface ActionFlowReporter extends EventTracker {
  setNextTracking(tracking: ActionFlowTracking): void
  trackScreeningCancel(chainId: number): void
  trackSimulationFailure(chainId: number, actionType: string, error: unknown): void
  trackStepSettled(settlement: ActionFlowStepSettlement): void
  setSignatureCount(count: number): void
  /** `pendingTxHash`: the on-chain hash ActionFlow shows while `stepIndex` is pending, if any. */
  reportProgress(stepIndex: number, pendingTxHash?: Hash): void
  trackCancel(stepIndex: number, chainId: number | undefined, stepCount: number): void
  /** Logs a last step still waiting for COMPLETED, e.g. when the provider unmounts. */
  releaseHeldStep(): void
}

const actionFlowEventPropertiesSchema = z.object({
  submissionId: z.string().optional(),
  chainId: z.number().optional(),
  totalSteps: z.number().optional(),
  startingStepIndex: z.number().optional(),
  stepIndex: z.number().optional(),
  status: z.string().optional(),
  phase: z.string().optional(),
  error_message: z.string().optional(),
  revertErrorName: z.string().optional(),
  txHash: z.custom<Hash>(val => typeof val === 'string').nullish()
})

interface ActiveActionFlow {
  submissionId: string
  tracking: ActionFlowTracking
  chainId: number
  stepCount: number
  signatureCount: number
  nextStepIndex: number
  stepTxHashes: Map<number, Hash>
  /** The last step confirmed with no hash yet; it waits for COMPLETED's hash. */
  isLastStepHeld: boolean
  stepSettlements: Map<number, ActionFlowStepSettlement>
}

function trackScreeningCancelled(tracking: ActionFlowTracking, chainId: number) {
  if (!tracking.checkout) return
  trackTransactionFlowCancelled({
    tracking: tracking.checkout,
    chainId,
    walletType: tracking.walletType,
    step: 'screening'
  })
}

/** @internal */
export function createActionFlowReporter({
  waitForReceipt
}: {
  waitForReceipt: (chainId: number, hash: Hash) => Promise<TransactionReceipt>
}): ActionFlowReporter {
  let next: ActionFlowTracking | undefined
  let pending: ActionFlowTracking | undefined
  let signatureCount = 0
  let active: ActiveActionFlow | undefined
  let failed: ActiveActionFlow | undefined

  function normalizeReportedError(error: Error): Error {
    return error.cause instanceof SimulationError ? error.cause : error
  }

  function getStepTracking(activeFlow: ActiveActionFlow, stepIndex: number) {
    const stepType = getCheckoutStepType({
      stepIndex,
      signatureCount: activeFlow.signatureCount,
      stepCount: activeFlow.stepCount
    })
    const actionType = getStepActionType(activeFlow.tracking, stepType)
    const checkout = activeFlow.tracking.checkout

    return {
      stepType,
      actionType,
      checkout,
      funnelStepIndex: getCheckoutStepIndex(stepIndex, stepType, checkout),
      transaction: {
        chainId: activeFlow.chainId,
        actionType,
        flowActionType: activeFlow.tracking.actionType,
        txSource: activeFlow.tracking.txSource,
        flow: activeFlow.tracking.flow,
        txFlag: activeFlow.tracking.txFlag,
        // The exit belongs to the final step only — approvals and signatures carry none.
        exitType: stepType === 'final' ? activeFlow.tracking.exitType : undefined,
        walletType: activeFlow.tracking.walletType,
        walletConnector: activeFlow.tracking.walletConnector,
        vaultAddress: activeFlow.tracking.vaultAddress,
        reviewSessionId: checkout?.reviewSessionId,
        attemptId: checkout?.attemptId
      }
    }
  }

  function trackFinalSigned(
    flow: ActiveActionFlow,
    transaction: ReturnType<typeof getStepTracking>['transaction'],
    checkout: CheckoutAttemptTrackingContext | undefined,
    receipt: ActionFlowReceipt
  ) {
    trackTransactionSigned({
      txHash: receipt.transactionHash,
      chainId: transaction.chainId,
      chainName: flow.tracking.chainName,
      walletType: transaction.walletType,
      transactionCount: 1,
      simulationBypassed: flow.tracking.getSimulationBypassed?.() ?? false,
      checkoutTracking: checkout,
      exitType: transaction.exitType
    })
  }

  function reportSuccess(
    flow: ActiveActionFlow,
    stepIndex: number,
    txHash?: Hash,
    resolveFunnel = true
  ) {
    txHash ??= flow.stepTxHashes.get(stepIndex)

    const { stepType, actionType, checkout, funnelStepIndex, transaction } = getStepTracking(
      flow,
      stepIndex
    )
    const settlement = flow.stepSettlements.get(stepIndex)
    const receipt = settlement && settlement.status !== 'failed' ? settlement.receipt : undefined
    trackTransaction({
      ...transaction,
      txHash: receipt?.transactionHash ?? txHash,
      ...getGasTelemetry(settlement),
      outcome: 'success'
    })
    if (stepType === 'final' && receipt) {
      trackFinalSigned(flow, transaction, checkout, receipt)
      trackTransactionConfirmed({
        txHash: receipt.transactionHash,
        chainId: transaction.chainId,
        chainName: flow.tracking.chainName,
        blockNumber: receipt.blockNumber.toString(),
        operationTypes: [actionType],
        walletType: transaction.walletType,
        exitType: transaction.exitType
      })
    }
    if (resolveFunnel) {
      maybeTrackStepResolved(checkout, {
        chainId: transaction.chainId,
        walletType: transaction.walletType,
        stepType,
        stepIndex: funnelStepIndex,
        outcome: 'completed'
      })
    }
  }

  function settledError(stepIndex: number) {
    const settlement = active?.stepSettlements.get(stepIndex)
    return settlement && settlement.status !== 'success' ? settlement.error : undefined
  }

  function reportFailure(
    flow: ActiveActionFlow,
    stepIndex: number,
    error: Error,
    txHash?: Hash,
    resolveFunnel = true
  ) {
    // A retry after an `onSuccess` throw starts past the last step, which already succeeded.
    if (stepIndex >= flow.stepCount) return

    const reportedError = normalizeReportedError(error)
    const finalStepIndex = flow.stepCount - 1
    const failureStepIndex =
      reportedError instanceof SimulationError && stepIndex !== finalStepIndex
        ? finalStepIndex
        : stepIndex

    // The engine can fail a step before the stepIndex effect reports the ones before it.
    if (flow === active) reportProgress(failureStepIndex !== stepIndex ? stepIndex + 1 : stepIndex)
    txHash ??= flow.stepTxHashes.get(stepIndex)
    const { stepType, actionType, checkout, funnelStepIndex, transaction } = getStepTracking(
      flow,
      failureStepIndex
    )
    const settlement = flow.stepSettlements.get(failureStepIndex)
    const receipt = settlement && settlement.status !== 'failed' ? settlement.receipt : undefined
    txHash = receipt?.transactionHash ?? txHash
    logTransactionError(reportedError, {
      chainId: transaction.chainId,
      actionType,
      walletType: transaction.walletType,
      ...(txHash && { txHash })
    })
    trackTransaction({
      ...transaction,
      ...(txHash && { txHash }),
      ...getGasTelemetry(settlement),
      outcome: 'failure',
      error: reportedError
    })
    if (stepType === 'final') {
      if (receipt) trackFinalSigned(flow, transaction, checkout, receipt)
      trackTransactionFailed({
        txHash,
        chainId: transaction.chainId,
        chainName: flow.tracking.chainName,
        operationTypes: [actionType],
        walletType: transaction.walletType,
        error: reportedError,
        exitType: transaction.exitType
      })
    }
    if (resolveFunnel) {
      trackCheckoutStepFailure({
        tracking: checkout,
        error: reportedError,
        chainId: transaction.chainId,
        chainName: flow.tracking.chainName,
        walletType: transaction.walletType,
        stepType,
        stepIndex: funnelStepIndex,
        rejectionStep: stepType,
        operationTypes: [actionType]
      })
    }
  }

  function watchReceipt(
    flow: ActiveActionFlow,
    finalStepIndex: number,
    hash: Hash,
    {
      resolveFunnel,
      onGiveUp
    }: {
      resolveFunnel: boolean
      onGiveUp: (error: unknown) => void
    }
  ) {
    void waitForReceipt(flow.chainId, hash).then(receipt => {
      if (receipt.status === 'success') {
        flow.stepSettlements.set(finalStepIndex, {
          submissionId: flow.submissionId,
          stepIndex: finalStepIndex,
          status: 'success',
          receipt
        })
        reportSuccess(flow, finalStepIndex, hash, resolveFunnel)
        flow.tracking.onConfirmedLate?.()
      } else {
        reportFailure(
          flow,
          finalStepIndex,
          new Error('Transaction reverted on-chain'),
          hash,
          resolveFunnel
        )
      }
    }, onGiveUp)
  }

  function reportProgress(stepIndex: number, pendingTxHash?: Hash) {
    if (!active) return
    // The flow was reset while its last step waited for COMPLETED: that step still succeeded.
    if (active.isLastStepHeld && stepIndex < active.stepCount) {
      releaseHeldStep()
      return
    }
    // A contract wallet's pending hash can be an off-chain safeTxHash.
    if (pendingTxHash && active.tracking.walletType === 'eoa') {
      active.stepTxHashes.set(stepIndex, pendingTxHash)
    }
    reportThrough(stepIndex)
  }

  function releaseHeldStep() {
    if (!active?.isLastStepHeld) return
    reportThrough(active.stepCount, {})
    active = undefined
  }

  /**
   * `final` is passed once the flow ends; its `txHash` is the engine's COMPLETED hash, so it only
   * belongs to the last step. Without `final` the last step is held until COMPLETED brings that
   * hash, which a contract-wallet transaction can't get from its pending hash.
   */
  function reportThrough(stepIndex: number, final?: { txHash?: Hash }) {
    if (!active) return

    const targetStepIndex = Math.min(stepIndex, active.stepCount)
    while (active.nextStepIndex < targetStepIndex) {
      const isLastStep = active.nextStepIndex === active.stepCount - 1
      if (isLastStep && !final) {
        active.isLastStepHeld = true
        return
      }
      reportSuccess(active, active.nextStepIndex, isLastStep ? final?.txHash : undefined)
      active.nextStepIndex += 1
    }
  }

  return {
    track(event, properties) {
      analyticsEventTracker.track(event, properties)
      const parsedProperties = actionFlowEventPropertiesSchema.safeParse(properties)
      if (!parsedProperties.success) return

      const parsed = parsedProperties.data
      if (event === ACTION_FLOW_EVENTS.STARTED) {
        if (
          next &&
          parsed.submissionId !== undefined &&
          parsed.chainId !== undefined &&
          parsed.totalSteps !== undefined
        ) {
          releaseHeldStep()
          active = {
            submissionId: parsed.submissionId,
            tracking: next,
            chainId: parsed.chainId,
            stepCount: parsed.totalSteps,
            signatureCount,
            nextStepIndex: parsed.startingStepIndex ?? 0,
            stepTxHashes: new Map(),
            isLastStepHeld: false,
            stepSettlements: new Map()
          }
          trackTransactionInitiated({
            chainId: parsed.chainId,
            chainName: next.chainName,
            operationTypes: [next.actionType],
            isSimpleTx: false,
            walletType: next.walletType,
            exitType: next.exitType
          })
          pending = undefined
          failed = undefined
        }
        return
      }

      if (!active || parsed.submissionId !== active.submissionId) return

      if (event === ACTION_FLOW_EVENTS.COMPLETED && parsed.status === 'success') {
        reportThrough(active.stepCount, { txHash: parsed.txHash ?? undefined })
        failed = undefined
        active = undefined
      } else if (event === ACTION_FLOW_EVENTS.COMPLETED && parsed.status === 'reverted') {
        const stepIndex = parsed.stepIndex ?? active.nextStepIndex
        reportFailure(
          active,
          stepIndex,
          settledError(stepIndex) ??
            new Error(
              `Transaction reverted on-chain${parsed.revertErrorName ? `: ${parsed.revertErrorName}` : ''}`
            ),
          parsed.txHash ?? undefined
        )
        failed = active
        active = undefined
      } else if (event === ACTION_FLOW_EVENTS.ERROR) {
        if (parsed.phase === 'on-success') {
          reportThrough(active.stepCount, {})
        } else {
          const flow = active
          const stepIndex = parsed.stepIndex ?? flow.nextStepIndex
          const error =
            settledError(stepIndex) ?? new Error(parsed.error_message ?? 'ActionFlow error')
          const finalStepIndex = flow.stepCount - 1
          const hash = flow.stepTxHashes.get(finalStepIndex)
          if (stepIndex === finalStepIndex && hash) {
            const { transaction } = getStepTracking(flow, finalStepIndex)
            const settlement = flow.stepSettlements.get(finalStepIndex)
            trackTransaction({
              ...transaction,
              txHash: hash,
              ...getGasTelemetry(settlement),
              outcome: 'receipt_timeout_pending'
            })
            watchReceipt(flow, finalStepIndex, hash, {
              resolveFunnel: true,
              onGiveUp: () => reportFailure(flow, finalStepIndex, error, hash)
            })
          } else {
            reportFailure(flow, stepIndex, error)
          }
          failed = flow
        }
        active = undefined
      } else if (event === ACTION_FLOW_EVENTS.USER_REJECTED) {
        if (parsed.phase === 'on-success') {
          reportThrough(active.stepCount, {})
        } else {
          const stepIndex = parsed.stepIndex ?? active.nextStepIndex
          reportFailure(
            active,
            stepIndex,
            settledError(stepIndex) ??
              new UserRejectedRequestError(new Error('User rejected the request.'))
          )
          failed = active
        }
        active = undefined
      } else if (isSafeFailureEvent(event)) {
        const stepIndex = parsed.stepIndex ?? active.nextStepIndex
        const error = settledError(stepIndex)
        const safeFailure = SAFE_FAILURE_ERRORS[event](error)
        reportFailure(
          active,
          stepIndex,
          event === ACTION_FLOW_EVENTS.SAFE_PROPOSAL_DROPPED ? safeFailure : (error ?? safeFailure)
        )
        failed = active
        active = undefined
      }
    },
    setNextTracking(tracking) {
      next = tracking
      pending = tracking
      failed = undefined
    },
    trackScreeningCancel(chainId) {
      if (next) trackScreeningCancelled(next, chainId)
      pending = undefined
    },
    trackSimulationFailure(chainId, actionType, error) {
      if (next?.actionType !== actionType) return

      trackTransaction({
        chainId,
        actionType,
        txSource: next.txSource,
        flow: next.flow,
        txFlag: next.txFlag,
        exitType: next.exitType,
        walletType: next.walletType,
        walletConnector: next.walletConnector,
        vaultAddress: next.vaultAddress,
        reviewSessionId: next.checkout?.reviewSessionId,
        attemptId: next.checkout?.attemptId,
        outcome: 'failure',
        error
      })
    },
    trackStepSettled(settlement) {
      if (!active || settlement.submissionId !== active.submissionId) return
      active.stepSettlements.set(settlement.stepIndex, settlement)
    },
    setSignatureCount(count) {
      signatureCount = count
    },
    reportProgress,
    releaseHeldStep,
    trackCancel(stepIndex, chainId, stepCount) {
      // A held last step is already confirmed; it only waits for COMPLETED's hash.
      if (active?.isLastStepHeld) reportThrough(active.stepCount, {})
      const cancelled = active ?? failed
      if (cancelled) {
        if (cancelled.tracking.checkout) {
          trackTransactionFlowCancelled({
            tracking: cancelled.tracking.checkout,
            chainId: cancelled.chainId,
            walletType: cancelled.tracking.walletType,
            step: getCheckoutStepType({
              stepIndex,
              signatureCount: cancelled.signatureCount,
              stepCount: cancelled.stepCount
            })
          })
        }
      } else if (pending && chainId !== undefined) {
        if (pending.checkout) {
          trackTransactionFlowCancelled({
            tracking: pending.checkout,
            chainId,
            walletType: pending.walletType,
            step: getCheckoutStepType({ stepIndex, signatureCount, stepCount })
          })
        }
      }
      const detached = active
      if (
        detached &&
        !detached.isLastStepHeld &&
        detached.nextStepIndex === detached.stepCount - 1
      ) {
        const finalStepIndex = detached.stepCount - 1
        const hash = detached.stepTxHashes.get(finalStepIndex)
        if (hash) {
          watchReceipt(detached, finalStepIndex, hash, {
            resolveFunnel: false,
            onGiveUp: error => {
              logger.warn('Failed to wait for a cancelled ActionFlow receipt', {
                chainId: detached.chainId,
                extra: { txHash: hash, error: String(error) }
              })
            }
          })
        }
      }
      pending = undefined
      active = undefined
      failed = undefined
    }
  }
}

function getGasTelemetry(settlement: ActionFlowStepSettlement | undefined) {
  if (!settlement || settlement.status === 'failed' || !settlement.gas) return {}

  return {
    ...(settlement.gas.gasEstimate !== undefined && {
      gasEstimate: Number(settlement.gas.gasEstimate)
    }),
    ...(settlement.gas.gasLimit !== undefined && { gasLimit: Number(settlement.gas.gasLimit) }),
    gasUsed: Number(settlement.receipt.gasUsed),
    gasLimitSource: settlement.gas.gasLimitSource,
    ...(settlement.gas.gasEstimateError !== undefined && {
      gasEstimateError: settlement.gas.gasEstimateError
    })
  }
}

function getStepActionType(tracking: ActionFlowTracking, stepType: CheckoutStepType) {
  if (stepType === 'signature') return 'signature'
  if (stepType === 'approval') return 'approval_tx'
  return tracking.actionType
}

function getCheckoutStepIndex(
  stepIndex: number,
  stepType: CheckoutStepType,
  checkout: CheckoutAttemptTrackingContext | undefined
) {
  // Legacy numbers steps from 1 and reports the final step at totalSteps.
  if (stepType === 'final' && checkout) return checkout.totalSteps
  return stepIndex + 1
}
